/* eslint-disable complexity */
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { checkAddress } from '@r1-frontend/entities/ContactAddress/actions/checkAddress';
import { resetAddress } from '@r1-frontend/entities/ContactAddress/actions/resetAddress';
import { EVerificationStatus } from '@r1-frontend/entities/ContactAddress/initialState';
import { getAddressByStr } from '@r1-frontend/entities/ContactAddress/selectors/helpers';

import { useWebp } from '@r1-frontend/shared/hooks/useWebp';

import { BgThrobber } from '~/src/components/ThrobberWithBg';
import { useAnalyticsSender } from '~/src/helpers/hooks/useAnaliticsSender';
import { TState } from '~/src/store';
import { selectCompanyName } from '~/src/store/selectors/city';

import { EConnectStatuses, titles } from './ResultForm/constants';
import { DEFAULT_IMAGE_MAP_JPG, DEFAULT_IMAGE_MAP_WEBP } from './constants';
import EditableAddressForm from './EditableAddressForm';
import { ResultForm } from './ResultForm';

import * as ST from './styled';

const CheckAddressMapConnect = (): JSX.Element => {
    const dispatch = useDispatch();
    const sendAnalytics = useAnalyticsSender('address verification');
    const isWepbSupported = useWebp();

    const {
        addressStatus,
        verifiedAddressData,
        currentCity,
        companyName,
    } = useSelector((state: TState) => ({
        addressStatus: state.contactAddress.verifiedAddressData.status,
        verifiedAddressData: state.contactAddress.verifiedAddressData,
        currentCity: state.city.currentCity,
        companyName: selectCompanyName(state),
    }));

    const connectStatus = useMemo(() => {
        const addressStr = getAddressByStr(verifiedAddressData.address);
        const { isHouseConnected, gigabitAvail, status } = verifiedAddressData;

        if (status !== EVerificationStatus.verified) {
            return EConnectStatuses.DEFAULT;
        }

        if (!isHouseConnected) {
            sendAnalytics('error', addressStr);
            return EConnectStatuses.NOT_CONNECT;
        }

        if (!gigabitAvail) {
            sendAnalytics('success_only_100', addressStr);
            return EConnectStatuses.NOT_GIGABIT;
        }

        sendAnalytics('success', addressStr);
        return EConnectStatuses.CONNECT;
    }, [verifiedAddressData]);

    useEffect(() => {
        return () => {
            dispatch(resetAddress());
        };
    }, []);

    const checkAddressConnect = useCallback(() => dispatch(checkAddress()), []);

    const cityImage = isWepbSupported ? currentCity.image_map_webp : currentCity.image_map;
    const defaultImage = isWepbSupported ? DEFAULT_IMAGE_MAP_WEBP : DEFAULT_IMAGE_MAP_JPG;

    return (
        <ST.CheckAddressConnect imgSrc={cityImage || defaultImage} className={'ym-hide-content'}>
            <ST.Title>{titles[connectStatus]}</ST.Title>

            {addressStatus !== EVerificationStatus.verified &&
                <>
                <ST.Text data-test='not-verified-message'>
                    Укажите ваш адрес, а&nbsp;мы&nbsp;проверим, подключён&nbsp;ли ваш дом к&nbsp;{companyName} и&nbsp;подберём подходящий тариф.
                </ST.Text>
                <EditableAddressForm
                    data-test='address-form'
                    onConfirmBtnClick={checkAddressConnect}
                />
                </>
            }

            {addressStatus === EVerificationStatus.verified &&
                <ResultForm
                    connectStatus={connectStatus}
                />
            }

            {addressStatus === EVerificationStatus.verifying && <BgThrobber/>}
        </ST.CheckAddressConnect>
    );
};

export default CheckAddressMapConnect;
